/**
 * Questo component è il 4 tentativo di creare una select
 * con cui l'utente può selezionare la commodity
 * tra una lista di commodities
 * 
 * i primi 3 sono stati:
 *   amd-select-center
 *   amd-select-center-local
 *   amd-select-center-generic
 * 
 * La lista di commodities può essere passata da fuori
 * serve solo in casi eccezionali
 * altrimenti viene presa internamente, dal profilo corrente
 */

import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild, ViewEncapsulation, } from '@angular/core';
import { MatSelect } from '@angular/material/select';
import { Utilities as U } from '../../utilities/utilities.model';
import { CommodityModel } from '@shared/models/commodity.model';
import { PermissionService } from '@shared/service/permission.service';
import { TranslateContext, TranslatePipe } from '@shared/pipes/translate.pipe';
import { UserPermissionsModel } from '@app/shared/models/user-permission.model';
import { logger } from '@app/shared/utilities/logger.model';
import { CommoditiesService } from '@app/shared/service/commodities.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

const commodities_list__mock: any = require('@mock/shared/commodities-list.mock.json');

@Component({
	selector: 'amd-center-list-select',
	templateUrl: './center-list-select.component.html',
	styleUrls: ['./center-list-select.component.scss'],
	encapsulation: ViewEncapsulation.None,
	changeDetection: ChangeDetectionStrategy.OnPush
})

export class CenterListSelectComponent implements OnInit, OnChanges {

	@Input() centerList?: CommodityModel[];
	@Input() required: boolean;
	@Output() getCenterSelected = new EventEmitter<CommodityModel>();
	
	private isAC: boolean;
	private userPermission: UserPermissionsModel;

	fakeCommodity: CommodityModel = new CommodityModel({ald_commodity_id: CommodityModel.allCentersID});
	t = TranslateContext;
	form: FormGroup;
	formField: typeof EFormField = EFormField;
	labelFakeCommodity: string;

	constructor(
		public permissionService: PermissionService,
		public translatePipe: TranslatePipe,
		private commoditiesService: CommoditiesService,
		private fb: FormBuilder,
	) {
		this.userPermission = this.permissionService.getCurrentUserPermissions();
		this.isAC = this.userPermission.isCenterOperator2();

		this.form = fb.group({
			[EFormField.center_list]: [{value: null}, Validators.required],
		});
	}

	ngOnInit(): void {
		this.centerList = this.handleInputCommodities(this.centerList);

		this.labelFakeCommodity = this.centerList.length > 1
			? this.translatePipe.transform('TUTTI_I_CENTRI', this.t.SHARED)
			: this.translatePipe.transform('NESSUN_CENTRO_DISPONIBILE', this.t.SHARED);
	}
	
	ngOnChanges(changes: SimpleChanges): void {
		
		if (!U.isEmpty(changes.centerList) && !U.isEmpty(changes.centerList.currentValue)) {
			this.centerList = this.handleInputCommodities(changes.centerList.currentValue);
		}
	}

	/**
	 * Listener select dei centri
	 * 
	 * @param {any} event
	 */
	onSelectChange(event: any) {

		// const commodity: CommodityModel = event.value;
		const commodity = event.value;

		// logger.log(commodity);
		this.setCommodity(commodity);
	}


	// LOGICA DI BUSINESS - da mettere in un file a parte
	
	/**
	 * Gestisce le commodities in input: 
	 * 
	 * 1) Se non ci sono commodities in input le prende dal profilo. 
	 * 
	 * 2) Restituisce la prima commodity dell'elenco: 
	 * dev'essere fatto al caricamento della pagina
	 * è utile quando non vengono passate le commodity da fuori
	 * 
	 * 3) seleziona e restituisce subito la prima commodity dell'elenco
	 * 
	 * @param {CommodityModel[]} commodityList
	 * @returns {CommodityModel[]}
	 */
	private handleInputCommodities(list: CommodityModel[]): CommodityModel[] {

		list = this.getCommodityList(list);

		// MOCK
		// list = [];														// nessun centro selezinato | select disabilitata - rimane il messaggio di errore
		// list = [commodities_list__mock[0]];  // mostra il 1 centro  			| select disabilitata
		// list = commodities_list__mock;				// mostra il 1 centro  			| select abilitata
		
		// se non ci sono centri mostro il messaggio
		if (list.length === 0) {
			this.setCommodity(this.fakeCommodity);
			this.form.controls[EFormField.center_list].disable();
		}
		
		// con 1 solo centro non va aperta la select
		if (list.length === 1) {
			this.form.controls[EFormField.center_list].disable();
		}
		
		// va subito restituita la prima commodity dell'elenco
		if (!U.isEmpty(list)) {
			this.setCommodity(list[0]);
		}

		// logger.log(list);

		return list;
	}

	/**
	 * Recupera le commodities del profilo, se non ci sono da input.
	 * Per chi non è centro, le raggruppa per mostrare soltanto i centri effettivi
	 * 
	 * Prima le commodities veniva sempre passate da fuori
	 * questo ha portato a varie regressioni
	 * ora le prendiamo direttamente dal service
	 * tranne quando dev'essere applciato un filtro da fuori
	 * @returns {CommodityModel[]}
	 */
	private getCommodityList(commodities: CommodityModel[]): CommodityModel[] {

		// prende le commodities dal profilo corrente
		// se non vengono passate le commodities da fuori
		if (U.isEmpty(commodities)) {
			
			// questa cosa dovrebbe essere possibile solo per il centro assistenza
			// se sei ADMIN è scontato che vengano passate da fuori
			commodities = this.commoditiesService.getCommodityByCurrentProfile();
			
			// TEST - mock
			// commodities = require('./commodities-single.mock.json');
			// commodities = require('./commodities-list.mock.json');
			// logger.log(typeof commodities);
		}

		// raggruppa le commodities dello stesso centro
		// da una visualizzazione più sintetica dei centri (in input)
		// serve agli ADMIN, quando nella lista ci sono vari centri
		if (!this.isAC) {
			commodities = this.commoditiesService.clusteringCommodities(commodities);
		}
		
		return commodities;
	}

	/**
	 * Seleziona la commodity in input:
	 * 
	 * viene memorizzata nel form
	 * viene restituita al padre con l'EventEmitter
	 * 
	 * @param {CommodityModel} commodity
	 */
	setCommodity(commodity: CommodityModel) {

		if (U.isEmpty(commodity) 
			// nel browser mi arriva un Object
			// || !(commodity instanceof CommodityModel)
		) {
			return;
		}

		this.form.controls[EFormField.center_list].setValue(commodity);
		this.getCenterSelected.emit(commodity);
	}
}

enum EFormField {
	center_list = 'center_list',
}
